import React, { createContext, Dispatch, useReducer, useContext } from 'react';
import produce from 'immer';
import { AlertColor } from '@mui/material/Alert';

export interface IToast {
  id: any;
  name: AlertColor;
  description?: any;
}

export interface IToastState {
  toasts: IToast[];
}

const ToastStateContext = createContext<IToastState | undefined>(undefined);

type IToastAction =
  | { type: 'OPEN'; name: AlertColor; description: string }
  | { type: 'CLOSE'; id: number };

type ToastDispatch = Dispatch<IToastAction>;

const ToastDispatchContext = createContext<ToastDispatch | undefined>(
  undefined
);

const getInitialValues = (): IToastState => {
  return {
    toasts: []
  };
};

const reducer = (state: IToastState, action: IToastAction): IToastState => {
  switch (action.type) {
    case 'OPEN':
      return produce(state, (draft: IToastState) => {
        draft.toasts.push({
          name: action.name,
          description: action.description,
          id: new Date().getTime()
        });
      });

    case 'CLOSE':
      return produce(state, (draft: IToastState) => {
        let index = draft.toasts.findIndex((toast: { id: any }) =>
          Object.is(toast.id, action.id)
        );
        index !== -1 && draft.toasts.splice(index, 1);
      });

    default:
      throw new Error('Unhandled action');
  }
};

export const ToastContextProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [state, dispatch] = useReducer(reducer, getInitialValues());

  return (
    <ToastDispatchContext.Provider value={dispatch}>
      <ToastStateContext.Provider value={state}>
        {children}
      </ToastStateContext.Provider>
    </ToastDispatchContext.Provider>
  );
};

export const useToastState = () => {
  const state = useContext(ToastStateContext);

  if (state) {
    return state;
  } else {
    throw new Error('not-found toast-state');
  }
};

export const useToastDispatch = () => {
  const dispatch = useContext(ToastDispatchContext);

  if (dispatch) {
    return dispatch;
  } else {
    throw new Error('not-found toast-dispatch');
  }
};

export const useToastActions = () => {
  const dispatch = useContext(ToastDispatchContext);

  const openToast = (name: AlertColor, description: any) => {
    dispatch && dispatch({ type: 'OPEN', name: name, description });
  };

  const closeToast = (id: any) => {
    dispatch && dispatch({ type: 'CLOSE', id });
  };

  return { openToast, closeToast };
};
